import { Entity } from 'dexie';
import { DateHelper } from '../../utils/DateHelper';
import type { AppDB } from '../db';

interface VehicleInfo {
  brand?: string;
  model?: string;
  color?: string;
  vin?: string;
  immat?: string;
}

type ParsedVehicleInfo = Omit<VehicleInfo, 'vin'>;

type DeviceMode = 'FLEET' | 'ASSET' | 'SECURE' | 'SECURE&TRACK' | 'TRACK' | 'TRACK+' | 'SECURE&TRACK+';

class DeviceStats extends Entity<AppDB> {
  public uid_device!: number;
  public registration!: string;
  public available!: boolean;
  public buying_price!: number | null;
  public selling_price!: number | null;
  public conservation!: number;
  public monthly_base!: number;
  public daily_base!: number;
  public device_type!: number;
  public mileage!: number;
  public engine!: number;
  public avr_fuel_consumption!: number | null;
  public fk_fuel_type!: number | null;
  public vehicle_info!: string | null;
  public usage_time!: number;
  public used_since!: number;
  public month!: number;
  public month1!: number;
  public month2!: number;
  public month3!: number;
  public month4!: number;
  public monthEco!: number;
  public monthEco1!: number;
  public monthEco2!: number;
  public monthEco3!: number;
  public monthEco4!: number;
  public device_mode!: DeviceMode | null;
  public subscription_end_date!: number | null;

  public source = 'devicestats';

  /* constructor(dataBack) {
    Object.assign(this, {
      ...dataBack,
           vehicle_info: dataBack?.vehicle_info ? JSON.stringify(dataBack.vehicle_info) : null,
       conservation: toMonths(dataBack?.conservation),
      used_since: toMonths(dataBack?.used_since),
      usage_time: dataBack?.device_type === 0 ? dataBack?.usage_time : toHours(dataBack?.usage_time),
      monthly_base: toDays(dataBack?.monthly_base),
      daily_base: dataBack?.device_type === 0 ? dataBack?.daily_base : toHours(dataBack?.daily_base),
      fuel_type: dataBack?.fk_fuel_type,
    });
  } */

  public normalize() {
    // Intentionally left blank.
  }

  // vehicle_info
  get parsedVehicleInfo() {
    const vehicle_info: VehicleInfo | null = this.vehicle_info ? JSON.parse(this.vehicle_info) : null;
    if (vehicle_info?.vin) delete vehicle_info.vin;
    return vehicle_info as ParsedVehicleInfo | null; // To force the type to be ParsedVehicleInfo, which doesn't include vin, since we delete it for security reasons
  }

  // conservation
  get conservationInMonths(): number {
    return DateHelper.toMonths(this.conservation);
  }
  set conservationInSeconds(value: number) {
    this.conservation = DateHelper.monthsToSeconds(value);
  }

  get usedSinceInMonths(): number {
    return DateHelper.toMonths(this.used_since);
  }

  get normalizedUsageTime(): number {
    return this.device_type === 0 ? this.usage_time : DateHelper.toHours(this.usage_time);
  }

  get monthlyBaseInDays(): number {
    return DateHelper.toDays(this.monthly_base);
  }

  set monthlyBaseInSeconds(value: number) {
    this.monthly_base = DateHelper.daysToSeconds(value);
  }

  get normalizedDailyBase(): number {
    return this.device_type === 0 ? this.daily_base : DateHelper.toHours(this.daily_base);
  }

  get fuel_type() {
    return this.fk_fuel_type;
  }
  set fuel_type(value: number | null) {
    this.fk_fuel_type = value;
  }

  public getMaintenanceEstimatedCostPerKm(estimatedMaintenanceCost: number) {
    if (this.daily_base !== 0 || (this.monthly_base !== 0 && this.conservation !== 0)) {
      const estimated =
        Number(
          (
            estimatedMaintenanceCost /
            (this.normalizedDailyBase * this.monthlyBaseInDays * this.conservationInMonths)
          ).toFixed(2)
        ) * 100;
      return estimated === Infinity || isNaN(estimated) ? 0 : estimated;
    } else return 0;
  }

  public getMaintenanceRealCostPerKm(realMaintenanceCost: number): number {
    if (
      this.device_type === 0 ||
      (this.device_type === 1 &&
        this.usage_time > 1 &&
        this.used_since * DateHelper.DAY_BY_MONTH > 2 &&
        this.conservation !== 0)
    ) {
      const realCost =
        Number(
          (
            realMaintenanceCost /
            ((this.normalizedUsageTime / this.usedSinceInMonths) * this.conservationInMonths)
          ).toFixed(2)
        ) * 100;
      return realCost === Infinity || isNaN(realCost) ? 0 : realCost;
    } else return 0;
  }

  private _isRealCostEligible(): boolean {
    return (
      this.device_type === 0 ||
      (this.device_type === 1 &&
        this.normalizedUsageTime > 1 &&
        this.usedSinceInMonths * DateHelper.DAY_BY_MONTH > 2)
    );
  }

  public isInDeviceModes(modes: readonly DeviceMode[]): boolean {
    return false;
    /* return this.device_mode !== null && modes.includes(this.device_mode); */
  }

  get cost() {
    if (
      this.buying_price !== 0 &&
      this.selling_price !== 0 &&
      this.conservation !== 0 &&
      this.daily_base !== 0 &&
      this.monthly_base !== 0
    ) {
      const cost =
        Number(
          (
            (this.buying_price - this.selling_price) /
            (this.normalizedDailyBase * this.monthlyBaseInDays * this.conservationInMonths)
          ).toFixed(2)
        ) * 100;
      return cost === Infinity || isNaN(cost) ? 0 : cost;
    } else return 0;
  }

  set cost(value: number) {
    // Intentionally left blank.
  }

  get realCost() {
    if (
      this.buying_price !== 0 &&
      this.selling_price !== 0 &&
      this.conservation !== 0 &&
      this._isRealCostEligible()
    ) {
      const realCost =
        Number(
          (
            (this.buying_price - this.selling_price) /
            ((this.normalizedUsageTime / this.usedSinceInMonths) * this.conservationInMonths)
          ).toFixed(2)
        ) * 100;

      return realCost === Infinity || isNaN(realCost) ? 0 : realCost;
    } else return 0;
  }

  set realCost(value: number) {
    // Intentionally left blank.
  }
}

export default DeviceStats;
